import ProductCard from "../ProductCard";
import { useNavigate } from "react-router";
import { useEffect, useState } from "react";

//import CSS
import "./Cart.css";

//components
import CheckoutFooter from "./CheckoutFooter";

//custom hooks
import { useAuthContext } from "../../hooks/useAuthContext";
import { useCart } from "../../hooks/useCart";
import getAuthHeaders from "../../utils/getAuthHeaders";
import { useRoute } from "../../hooks/useRoute";
import { useUser } from "../../hooks/useUser";
import { usePreviousOrder } from "../../hooks/usePreviousOrder";
import { Link } from "react-router-dom";

// Helper function to format date from MM/DD/YYYY to "Mon, Mar 3, 2026"
const formatOrderDeadline = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const options = { weekday: "short", month: "short", day: "numeric" };
  return date.toLocaleDateString("en-US", options);
};

const Cart = () => {
  const navigate = useNavigate();
  const { cart, cartTotal, refetchCart } = useCart();
  const { user: userAuth } = useAuthContext();
  const [hydratedCart, setHydratedCart] = useState([]);
  const [priceChanges, setPriceChanges] = useState([]);
  const [outOfStock, setOutOfStock] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [hasHydrated, setHasHydrated] = useState(false);

  const HandleCheckout = () => {
    navigate("/cart/fulfillment"), { replace: true };
  };

  const { route } = useRoute();
  const { user } = useUser();
  const { previousOrderInfo } = usePreviousOrder();

  const ordersOpen = route ? route.ordersOpen : null;
  const dropLocation =
    user?.location && route?.dropLocations
      ? route.dropLocations.find((city) => city.name === user.location)
      : null;

  // Hydrate cart with full product details when cart changes
  useEffect(() => {
    const fetchFullProductDetails = async () => {
      if (!cart || cart.length === 0) {
        setHydratedCart([]);
        setPriceChanges([]);
        setOutOfStock([]);
        setIsLoadingProducts(false);
        setHasHydrated(false);
        return;
      }

      if (!userAuth) {
        setIsLoadingProducts(false);
        return;
      }

      // If already hydrated, just update the hydratedCart with current cart items
      // This allows quantity changes and removals to reflect immediately
      if (hasHydrated) {
        // Filter hydratedCart to only include items still in cart
        const currentProductIds = cart.map((item) => item.productId);
        const updatedHydratedCart = ((prev) => {
          // Build new array from previous hydratedCart and current cart
          const filtered = prev
            .filter((item) => currentProductIds.includes(item.productId))
            .map((item) => {
              const cartItem = cart.find((c) => c.productId === item.productId);
              return {
                ...item,
                quantity: cartItem?.quantity || item.quantity,
              };
            });

          // If nothing changed (same length and same quantities), keep previous to avoid rerender loop
          if (
            filtered.length === prev.length &&
            filtered.every(
              (it, idx) =>
                it.productId === prev[idx].productId &&
                it.quantity === prev[idx].quantity
            )
          ) {
            return prev;
          }
          return filtered;
        })(hydratedCart);

        // Only set when different by reference (the IIFE already returns prev if identical)
        if (updatedHydratedCart !== hydratedCart)
          setHydratedCart(updatedHydratedCart);
        return;
      }

      try {
        setIsLoadingProducts(true);
        const productIds = cart.map((item) => item.productId);

        const authHeader = await getAuthHeaders(userAuth);

        const fetchUrl =
          process.env.NODE_ENV === "development"
            ? `${process.env.REACT_APP_NETWORK}/api/products/by-ids`
            : "/api/products/by-ids";

        const response = await fetch(fetchUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...authHeader,
          },
          body: JSON.stringify({
            productIds,
            cartItems: cart, // Pass cart items for price comparison
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch products: ${response.statusText}`);
        }

        const data = await response.json();
        setHydratedCart(data.products || []);
        setPriceChanges(data.priceChanges || []);
        setOutOfStock(data.outOfStock || []);
        setIsLoadingProducts(false);
        setHasHydrated(true);

        // If there are price changes or out-of-stock items, update the cart in database
        if (data.priceChanges.length > 0 || data.outOfStock.length > 0) {
          await updateCartInDatabase(data.products, authHeader);
        }
      } catch (error) {
        console.error("Error fetching product details:", error);
        setIsLoadingProducts(false);
      }
    };

    const updateCartInDatabase = async (products, authHeader) => {
      try {
        // Build updated cart with only available products and current prices
        const updatedCart = products
          .filter(
            (product) =>
              product.quantityAvailable > 0 &&
              product.inventoryStatus === "include"
          )
          .map((product) => ({
            productId: product.productId,
            quantity: product.quantity,
            casePrice: product.casePrice, // Update with current price
            unitPrice: product.unitPrice, // Update with current unit price
          }));

        const updateUrl =
          process.env.NODE_ENV === "development"
            ? `${process.env.REACT_APP_NETWORK}/api/cart`
            : "/api/cart";

        const response = await fetch(updateUrl, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...authHeader,
          },
          body: JSON.stringify({ cart: updatedCart }),
        });

        if (!response.ok) {
          throw new Error(`Failed to update cart: ${response.statusText}`);
        }

        console.log("Cart updated with current prices and available items");

        // Refetch cart to update cartTotal in context
        await refetchCart();
      } catch (error) {
        console.error("Error updating cart in database:", error);
      }
    };

    fetchFullProductDetails();
  }, [cart, userAuth, refetchCart, hasHydrated]);

  useEffect(() => {
    if (outOfStock.length > 0) {
      outOfStock.forEach((item) => {
        if (item.quantityAvailable === 0) {
          alert(`Sorry! The product ${item.description} is now out of stock!`);
        }
      });
    }
  }, [outOfStock]);

  const PriceChangeNotification = () => {
    if (priceChanges.length === 0) return null;

    return (
      <div
        style={{
          padding: "20px",
          margin: "20px 0",
          backgroundColor: "#fff3cd",
          border: "1px solid #ffc107",
          borderRadius: "5px",
        }}
      >
        <h3 style={{ color: "#856404", marginTop: 0 }}>
          ⚠️ Price Changes Detected
        </h3>
        <p style={{ color: "#856404" }}>
          Some items in your cart have changed price since you added them:
        </p>
        <ul style={{ color: "#856404", marginBottom: 0 }}>
          {priceChanges.map((change, index) => (
            <li key={index}>
              <strong>{change.description}</strong>:
              {change.casePriceChanged && (
                <div>
                  Case Price: ${(change.oldCasePrice / 100).toFixed(2)} → $
                  {(change.newCasePrice / 100).toFixed(2)}
                  {change.newCasePrice > change.oldCasePrice ? " 📈" : " 📉"}
                </div>
              )}
              {change.unitPriceChanged && (
                <div>
                  Unit Price: ${(change.oldUnitPrice / 100).toFixed(2)} → $
                  {(change.newUnitPrice / 100).toFixed(2)}
                  {change.newUnitPrice > change.oldUnitPrice ? " 📈" : " 📉"}
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const OutOfStockNotification = () => {
    if (outOfStock.length === 0) return null;

    return (
      <div
        style={{
          padding: "20px",
          margin: "20px 0",
          backgroundColor: "#f8d7da",
          border: "1px solid #dc3545",
          borderRadius: "5px",
        }}
      >
        <h3 style={{ color: "#721c24", marginTop: 0 }}>
          ❌ Items Out of Stock
        </h3>
        <p style={{ color: "#721c24" }}>
          The following items are currently unavailable:
        </p>
        <ul style={{ color: "#721c24", marginBottom: 0 }}>
          {outOfStock.map((item, index) => (
            <li key={index}>
              <strong>{item.description}</strong>
              {item.inventoryStatus === "removed"
                ? " (no longer available)"
                : " (out of stock)"}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const CartNote = () => {
    return (
      <ul>
        <li>
          <strong> • Order Schedule:</strong> You can browse our products
          anytime, but orders can only be placed during designated order
          windows.
        </li>
        <li>
          <strong> • Upcoming Order Window:</strong> Our next order window for{" "}
          {dropLocation.name} is {dropLocation.nextOrderWeek}. We'll send you a
          reminder when the order window opens if you've subscribed to our order
          reminders.
        </li>
        <li>
          <strong> • Important Inventory Note:</strong> Our inventory changes
          frequently, so some items may become unavailable by your order week.
          We're also adding new products daily, so you'll have fresh options
          when it's time to place your order!
        </li>
      </ul>
    );
  };

  return (
    <div>
      {<h1>Shopping Cart</h1>}
      <PriceChangeNotification />
      <OutOfStockNotification />
      {/*orders are closed*/}
      {!ordersOpen && dropLocation && (
        <div
          style={{
            padding: "50px",
            border: "1px solid #ddd",
            borderRadius: "5px",
            fontFamily: "sans-serif",
          }}
        >
          <h3>
            Hi, {user.firstName}! Your next order week is{" "}
            {dropLocation.nextOrderWeek} for {dropLocation.name} pick-ups.
          </h3>
          <CartNote />
        </div>
      )}
      {/*orders are open, but not for this drop location*/}
      {ordersOpen && dropLocation && !dropLocation.ordersOpen && (
        <div
          style={{
            padding: "50px",
            border: "1px solid #ddd",
            borderRadius: "5px",
            fontFamily: "sans-serif",
          }}
        >
          <h3>Hi, {user.firstName}!</h3>
          <h3>
            We are not currently delivering to your selected primary location.
            Please see other pickup times & locations{" "}
            <Link to="/pickup-times" className="navbar-notification-link">
              here
            </Link>
            . (Tip: You can update your primary location in your account
            settings.)
          </h3>
          <CartNote />
        </div>
      )}
      {/*orders are open for this drop location*/}
      {ordersOpen &&
        dropLocation &&
        dropLocation.ordersOpen &&
        previousOrderInfo && (
          <div
            style={{
              padding: "50px",
              border: "1px solid #ddd",
              borderRadius: "5px",
              fontFamily: "sans-serif",
            }}
          >
            <h3>
              Hi, {user.firstName}! You have{" "}
              <strong>
                {previousOrderInfo.daysRemaining < 1
                  ? "until end of day today"
                  : `${previousOrderInfo.daysRemaining} ${
                      previousOrderInfo.daysRemaining === 1 ? "day" : "days"
                    }`}
              </strong>{" "}
              remaining to add to your order for{" "}
              <strong>{previousOrderInfo.fulfillmentDate}</strong>. Happy
              shopping!
            </h3>
            <ul>
              <li>
                <strong> • Important Inventory Note:</strong> Our inventory
                changes frequently, so some items may become unavailable. We're
                also adding new products daily so you'll have new options to
                choose from!
              </li>
            </ul>
          </div>
        )}
      {ordersOpen &&
        dropLocation &&
        dropLocation.ordersOpen &&
        !previousOrderInfo && (
          <div
            style={{
              padding: "50px",
              border: "1px solid #ddd",
              borderRadius: "5px",
              fontFamily: "sans-serif",
            }}
          >
            <h3>
              Hi, {user.firstName}! Orders are open for {dropLocation.name}.
              Happy shopping!
            </h3>
            <ul>
              <li>
                <strong> • Order Deadline:</strong> Orders must be placed by the
                end of the day {formatOrderDeadline(route.orderDeadline)}.
              </li>
              <li>
                <strong> • Important Inventory Note:</strong> Our inventory
                changes frequently, so some items may become unavailable. We're
                also adding new products daily so you'll have new options to
                choose from!
              </li>
            </ul>
          </div>
        )}
      {cart.length === 0 && <h3>Cart is Empty</h3>}
      {isLoadingProducts && cart.length > 0 && <h3>Loading products...</h3>}
      <div className="product-grid shopping-cart">
        {!isLoadingProducts &&
          hydratedCart.length > 0 &&
          hydratedCart.map((item) => {
            return <ProductCard key={item._id} item={item}></ProductCard>;
          })}
      </div>
      <CheckoutFooter
        cart={cart}
        cartTotal={cartTotal}
        clickAction={HandleCheckout}
        text="Checkout"
      />
    </div>
  );
};

export default Cart;
